import * as yup from 'yup';
// Types
import { AccountSubType } from 'api/graphql-global-types';
// Context
import { SocialAccount } from 'context/OnboardingContext';
// Helpers
import { yupInternationalPhoneValidation } from 'helpers/validation';
import { isAthleteOrContentCreator } from 'helpers/accountType';
import { createUsernameValidation } from 'helpers/usernameValidation';
import { getSocialProfileLinkError } from 'helpers/socialMediaLink';

/**
 * Resolved at validation time rather than baked into the schema: the linked count comes from a
 * query and the modal flag from context, and both change while step 3 is on screen.
 */
export type OnboardingValidationContext = {
  linkedSocialAccountsCount: number;
  isAddSocialAccountModalOpen: boolean;
};

const readContext = (
  options: yup.ValidateOptions
): Partial<OnboardingValidationContext> =>
  (options.context as Partial<OnboardingValidationContext>) || {};

/**
 * A creator must arrive with at least one social account: either a link typed into the add modal
 * (`isAdded`) or a platform linked over OAuth. Typed links must point at a profile on the chosen
 * platform — the old schema accepted any non-empty string, which is how creators went live with
 * junk like "0" and no usable socials.
 */
const socialAccountsSchema = () =>
  yup
    .array()
    .of(
      yup.object().shape({
        platform: yup.string().when('isAdded', {
          is: false,
          then: yup
            .string()
            .test('is-required', 'Platform is required', function (value) {
              const { isAddSocialAccountModalOpen } = readContext(this.options);

              return !isAddSocialAccountModalOpen || !!value;
            }),
        }),
        link: yup
          .string()
          .test('is-social-profile-link', 'Link is required', function (value) {
            const { isAddSocialAccountModalOpen } = readContext(this.options);
            const { isAdded, platform } = (this.parent || {}) as SocialAccount;

            // The trailing draft row only has to hold up while the add modal is open
            if (!isAdded && !isAddSocialAccountModalOpen) {
              return true;
            }

            if (!value?.trim()) {
              return this.createError({ message: 'Link is required' });
            }

            const linkError = getSocialProfileLinkError(platform, value);

            return linkError ? this.createError({ message: linkError }) : true;
          }),
        isAdded: yup.boolean(),
      })
    )
    .test(
      'has-social-or-linked',
      'At least one social account is required',
      function (value) {
        const hasTyped = (value || []).some(
          (account: SocialAccount) => account?.isAdded
        );
        const hasLinked = Boolean(
          readContext(this.options).linkedSocialAccountsCount
        );

        return hasTyped || hasLinked;
      }
    );

export const onboardingValidationSchema = (step: number) => {
  if (step === 1) {
    return yup.object().shape({
      username: createUsernameValidation(),
      profileName: yup.string().required('Profile name is required'),
      avatar: yup.string().required('Avatar is required'),
    });
  }

  if (step === 2) {
    return yup.object().shape({
      accountType: yup.object().required('Account type is required'),
      experienceLevel: yup
        .object()
        .nullable()
        .when(['accountType'], {
          is: (accountType) => accountType?.value === AccountSubType.Athlete,
          then: yup
            .object()
            .shape({
              value: yup.string().required('Experience level is required'),
              label: yup.string().required('Experience level is required'),
            })
            .required('Experience level is required'),
        }),
      birthDate: yup
        .date()
        .nullable()
        .when(['accountType'], {
          is: (accountType) => isAthleteOrContentCreator(accountType),
          then: yup
            .date()
            .max(new Date(), 'Birthday cannot be in the future')
            .required('Birthday is required'),
        }),
      gender: yup
        .object()
        .nullable()
        .when(['accountType'], {
          is: (accountType) => isAthleteOrContentCreator(accountType),
          then: yup
            .object()
            .shape({
              value: yup.string().required('Gender is required'),
              label: yup.string().required('Gender is required'),
            })
            .required('Gender is required'),
        }),
      bio: yup.string().max(300, 'Bio must be less than 300 characters'),
      sport: yup.object().required('Sport is required'),
      phoneNumber: yupInternationalPhoneValidation(),
      phoneOptIn: yup
        .boolean()
        .oneOf([true])
        .required('This field is required'),
      location: yup.object().required('Location is required'),
    });
  }

  if (step === 3) {
    return yup.object().shape({
      socialAccounts: socialAccountsSchema(),
    });
  }

  return yup.object().shape({});
};
